/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import React, { Fragment } from 'react'
import './HomeHospiceSummary.scss'
import { Collapse, CardBody, Card, CardHeader, Container } from 'reactstrap'
import { IBaseSection, IBloodTypeClass, ISummary, ICoverageDiscoveryResponseStateManager } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { FacilitiesComponent } from '../../Medicare/Facilities/Facilities'
import { DeductiblesComponent } from '../../Medicare/Deductibles/Deductibles'
import { DescriptionsComponent } from '../../Medicare/Descriptions/Descriptions'
import { LimitationsComponent } from '../../Medicare/Limitations/Limitations'
import { Copays } from '../../Medicare/Copays/Copays'
import { CoInsurance } from '../../Medicare/CoInsurance/CoInsurance'
import { CommonUtils } from '../../../../utils/CommonUtils'
import parse from 'html-react-parser'

export interface IHomeHospiceSummaryProps {
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}

interface IHomeHospiceSummaryState {
	collapse: number
}

export class HomeHospiceSummaryComponent extends React.Component<IHomeHospiceSummaryProps, IHomeHospiceSummaryState> {
	private dateFormat = 'MM/DD/YYYY'
	constructor(props: Readonly<IHomeHospiceSummaryProps>) {
		super(props)
		this.toggle = this.toggle.bind(this)
		this.state = { collapse: 0 }
	}

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	toggle(e): void {
		const event = e.target.dataset.event
		this.setState({ collapse: this.state.collapse === Number(event) ? 0 : Number(event) })
	}

	render(): React.ReactNode {
		const section = this.props.CoverageDiscoveryResponseStateManager.selectedMedicareHomeHospiceSection
		if (!this.props.CoverageDiscoveryResponseStateManager.selectedMedicareHomeHospiceSection) return <></>
		else {
			const { collapse } = this.state
			return (
				<div className="accordion-group service-coverage">
					<div id={section.id} className="section" data-loadorder={section.loadOrder} data-index={section.summary.index}>
						<Card style={{ marginBottom: '1rem' }} className="card" key={section.displayOrder}>
							<CardHeader onClick={this.toggle} data-event={section.displayOrder} className="service-coverage accordion-heading whiteText">
								{section.summary && section.summary.title}
							</CardHeader>
							<Collapse isOpen={collapse === section.displayOrder}>
								<CardBody className="card-body">{this.renderHomeHospiceSection(section)}</CardBody>
							</Collapse>
						</Card>
					</div>
				</div>
			)
		}
	}

	renderHomeHospiceSection(section: IBaseSection): any {
		return (
			<Container style={{ margin: 0, padding: 0, width: '100%' }}>
				<div className="cdResponsePage">
					<table className="service-types-table">
						<tbody>
							<tr className="summaryDisplaying">
								<td colSpan={4}>
									Displaying {section.summary.selectedSections.length} of {section.summary.sections.length} sections
								</td>
							</tr>
						</tbody>
						<tbody className="summaryBody summaryBody0">
							<tr className="summarySection">
								<td colSpan={2}>
									<h4>Home Health Certification</h4>
								</td>
								<td className="summaryHide" colSpan={2}></td>
							</tr>
						</tbody>
						{this.renderHomeHealthCertification(section.summary)}
						<tbody className="summaryBody summaryBody1">
							<tr className="summarySection">
								<td colSpan={2}>
									<h4>Home Health Care</h4>
								</td>
								<td className="summaryHide" colSpan={2}></td>
							</tr>
						</tbody>

						{this.renderHomeHealthCareEpisodesExistance(section.summary.homeHealthTypes)}
						{section.summary.homeHealthTypes && this.renderHomeHealthCare(section.summary.homeHealthTypes)}
						<tbody className="summaryBody summaryBody2">
							<tr className="summarySection">
								<td colSpan={2}>
									<h4>Hospice</h4>
								</td>
								<td className="summaryHide" colSpan={2}></td>
							</tr>
						</tbody>
						{this.renderHospicePeriodsExistance(section.summary.hospiceTypes)}
						{section.summary.hospiceTypes && this.renderHospice(section.summary.hospiceTypes)}
					</table>
				</div>
			</Container>
		)
	}

	renderHospicePeriodsExistance(hospiceTypes?: IBloodTypeClass[]): any {
		if (!(hospiceTypes && hospiceTypes.some(x => x.facilities.length > 0))) {
			return (
				<tbody className="summaryBody summaryBody1">
					<tr>
						<td>&nbsp;</td>
						<td colSpan={3} className="bottom-border-td">
							No Hospice Periods found within the requested service dates
						</td>
					</tr>
				</tbody>
			)
		}
	}

	renderHospice(hospiceTypes: IBloodTypeClass[]): any {
		if (hospiceTypes) {
			return hospiceTypes.map((element, parentIndex) => {
				return (
					<Fragment key={parentIndex}>
						<FacilitiesComponent data={element.facilities} />
						{element.insurances.map((ins, index) => {
							return (
								<tbody key={index}>
									{ins.deductibles && <DeductiblesComponent data={ins.deductibles} />}
									<DescriptionsComponent data={ins} />
									<LimitationsComponent data={ins} />
								</tbody>
							)
						})}
					</Fragment>
				)
			})
		}
	}

	renderHomeHealthCareEpisodesExistance(homeHealthTypes: IBloodTypeClass[] | undefined): any {
		if (typeof homeHealthTypes === 'undefined' || homeHealthTypes.length === 0 || !this.contaisFacilities(homeHealthTypes)) {
			return (
				<tbody className="summaryBody summaryBody1">
					<tr>
						<td>&nbsp;</td>
						<td colSpan={3} className="bottom-border-td">
							No Home Health Episodes found within the requested service dates
						</td>
					</tr>
				</tbody>
			)
		}
	}
	renderHomeHealthCare(homeHealthTypes: IBloodTypeClass[]): any {
		if (typeof homeHealthTypes !== 'undefined' && homeHealthTypes !== null) {
			return homeHealthTypes.map((element, index) => {
				const insurancesWirthMB = element.insurances && element.insurances.filter(item => item.code && item.code === 'MB')
				return (
					<Fragment key={index + 'a'}>
						<FacilitiesComponent data={element.facilities} />
						{insurancesWirthMB &&
							insurancesWirthMB.map((ins, index2) => {
								return (
									<tbody key={index2}>
										{ins.deductibles && <DeductiblesComponent data={ins.deductibles} />}
										<Copays data={ins} />
										<CoInsurance data={ins} />
										<DescriptionsComponent data={ins} />
										<LimitationsComponent data={ins} />
									</tbody>
								)
							})}
					</Fragment>
				)
			})
		}
	}

	contaisFacilities(homeHealthTypes: IBloodTypeClass[]): boolean {
		let valueFound = false
		for (let index = 0; index < homeHealthTypes.length; index++) {
			const item = homeHealthTypes[index]
			if (item.facilities !== undefined && item.facilities.length > 0) {
				valueFound = true
				break
			}
		}
		return valueFound
	}

	renderHomeHealthCertification(Summary: ISummary): JSX.Element {
		if ((Summary.homeCertificationDates !== undefined && Summary.homeCertificationDates.length > 0) || (Summary.homeRecertificationDates !== undefined && Summary.homeRecertificationDates.length) > 0) {
			const dates = (input: Date[]) =>
				parse(
					input
						.sort((o1, o2) => CommonUtils.compareProperty(o2, o1))
						.map(item => CommonUtils.getDate(item, this.dateFormat))
						.join('<br />'),
				)

			return (
				<tbody>
					<tr>
						<td>Certification Dates:</td>
						<td>{Summary.homeCertificationDates ? dates(Summary.homeCertificationDates) : null}</td>
						<td>Recertification Dates:</td>
						<td>{Summary.homeRecertificationDates ? dates(Summary.homeRecertificationDates) : null}</td>
					</tr>
				</tbody>
			)
		} else {
			return (
				<tbody>
					<tr className="no-info">
						<td colSpan={4}>No Home Health Certification information available</td>
					</tr>
				</tbody>
			)
		}
	}
}
